import { createAsyncThunk } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '../../../shared/constants/loadingStatus';
import {
  createMessageTemplate as createMessageTemplateApi,
  createMessageTemplateForTenants as createMessageTemplateForTenantsApi,
  fetchMessageTemplateById as fetchMessageTemplateByIdApi,
  fetchMessageTemplatesFacets,
  fetchMessageTemplatesList,
  setMessageTemplateStatus as setMessageTemplateStatusApi,
  updateMessageTemplate as updateMessageTemplateApi,
} from '../api/messageTemplatesApi';
import { resolveChannelsForTab } from './lib/resolveChannelsForTab';
import { resolveTabKey } from './lib/resolveTabKey';
import { CHANNEL } from '../constants/messageTemplateKeys';
import {
  templatesListFailed,
  templatesListReceived,
  templatesListRequested,
  selectTemplatesListTabs,
} from './templatesListSlice';
import {
  templatesFacetsFailed,
  templatesFacetsReceived,
  templatesFacetsRequested,
  selectTemplatesFacetsTabs,
} from './templatesFacetsSlice';
import {
  selectTemplatesActiveTab,
  selectTemplatesFilters,
  selectTemplatesOrdering,
  selectTemplatesSearch,
  selectTemplatesTagsMode,
} from './templatesQuerySlice';
import {
  templatesErrorCleared,
  templatesErrorSet,
  templatesHasLoadedOnceSet,
} from './templatesQuerySlice';

export const loadMessageTemplates = createAsyncThunk(
  'templates/loadList',
  async (
    { tabKey, page, pageSize, search, ordering, filters, tagsMode } = {},
    { getState, dispatch, rejectWithValue, requestId },
  ) => {
    const state = getState();
    const resolvedTabKey = tabKey ?? selectTemplatesActiveTab(state) ?? CHANNEL.EMAIL;
    const isStale = () =>
      selectTemplatesListTabs(getState())?.[resolveTabKey(resolvedTabKey)]?.listRequestId !==
      requestId;
    const listTabs = selectTemplatesListTabs(state);
    const tabState = listTabs?.[resolvedTabKey] ?? {};

    const resolvedPage = page ?? tabState.page ?? 1;
    const resolvedPageSize = pageSize ?? tabState.pageSize ?? 15;
    const resolvedSearch = search ?? selectTemplatesSearch(state);
    const resolvedOrdering = ordering ?? selectTemplatesOrdering(state);
    const resolvedFilters = filters ?? selectTemplatesFilters(state);
    const resolvedTagsMode = tagsMode ?? selectTemplatesTagsMode(state);

    const effectiveChannels = resolveChannelsForTab({
      tabKey: resolvedTabKey,
      selected: resolvedFilters?.channels,
    });

    dispatch(templatesListRequested({ tabKey: resolvedTabKey, requestId }));
    dispatch(templatesErrorCleared());
    try {
      const payload = await fetchMessageTemplatesList({
        page: resolvedPage,
        pageSize: resolvedPageSize,
        search: resolvedSearch,
        ordering: resolvedOrdering,
        filters: {
          ...(resolvedFilters ?? {}),
          channels: effectiveChannels,
        },
        tagsMode: resolvedTagsMode,
      });
      if (isStale()) return payload;
      dispatch(
        templatesListReceived({
          tabKey: resolvedTabKey,
          requestId,
          payload,
        }),
      );
      dispatch(templatesHasLoadedOnceSet());
      return payload;
    } catch (error) {
      if (isStale()) return rejectWithValue(error?.message);
      dispatch(templatesListFailed({ tabKey: resolvedTabKey, requestId }));
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const loadMessageTemplateFacets = createAsyncThunk(
  'templates/loadFacets',
  async (
    { tabKey, search, ordering, filters, tagsMode } = {},
    { getState, dispatch, rejectWithValue, requestId },
  ) => {
    const state = getState();
    const resolvedTabKey = tabKey ?? selectTemplatesActiveTab(state) ?? CHANNEL.EMAIL;
    const isStale = () =>
      selectTemplatesFacetsTabs(getState())?.[resolveTabKey(resolvedTabKey)]?.facetsRequestId !==
      requestId;
    const resolvedSearch = search ?? selectTemplatesSearch(state);
    const resolvedOrdering = ordering ?? selectTemplatesOrdering(state);
    const resolvedFilters = filters ?? selectTemplatesFilters(state);
    const resolvedTagsMode = tagsMode ?? selectTemplatesTagsMode(state);

    const effectiveChannels = resolveChannelsForTab({
      tabKey: resolvedTabKey,
      selected: resolvedFilters?.channels,
    });

    dispatch(templatesFacetsRequested({ tabKey: resolvedTabKey, requestId }));
    dispatch(templatesErrorCleared());
    try {
      const payload = await fetchMessageTemplatesFacets({
        search: resolvedSearch,
        ordering: resolvedOrdering,
        filters: {
          ...(resolvedFilters ?? {}),
          channels: effectiveChannels,
        },
        tagsMode: resolvedTagsMode,
      });
      if (isStale()) return payload;
      dispatch(
        templatesFacetsReceived({
          tabKey: resolvedTabKey,
          requestId,
          payload,
        }),
      );
      return payload;
    } catch (error) {
      if (isStale()) return rejectWithValue(error?.message);
      dispatch(templatesFacetsFailed({ tabKey: resolvedTabKey, requestId }));
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const loadTemplatesPage = createAsyncThunk(
  'templates/loadPage',
  async ({ preload = 'both' } = {}, { getState, dispatch }) => {
    const state = getState();
    const listTabs = selectTemplatesListTabs(state);
    const facetsTabs = selectTemplatesFacetsTabs(state);
    const activeTab = selectTemplatesActiveTab(state);

    const tabKeys = preload === 'active' ? [activeTab] : [CHANNEL.EMAIL, 'text'];

    for (const tabKey of tabKeys) {
      const listStatus = listTabs?.[tabKey]?.listStatus ?? LOADING_STATUS.IDLE;
      if (listStatus === LOADING_STATUS.IDLE) {
        dispatch(loadMessageTemplates({ tabKey }));
      }

      const facetsStatus = facetsTabs?.[tabKey]?.facetsStatus ?? LOADING_STATUS.IDLE;
      if (facetsStatus === LOADING_STATUS.IDLE) {
        dispatch(loadMessageTemplateFacets({ tabKey }));
      }
    }
  },
);

export const createMessageTemplate = createAsyncThunk(
  'templates/create',
  async ({ payload } = {}, { dispatch, rejectWithValue }) => {
    dispatch(templatesErrorCleared());
    try {
      const response = await createMessageTemplateApi(payload ?? {});
      return response;
    } catch (error) {
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const createMessageTemplateForTenants = createAsyncThunk(
  'templates/createForTenants',
  async ({ payload } = {}, { dispatch, rejectWithValue }) => {
    dispatch(templatesErrorCleared());
    try {
      const response = await createMessageTemplateForTenantsApi(payload ?? {});
      return response;
    } catch (error) {
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const loadMessageTemplateById = createAsyncThunk(
  'templates/loadById',
  async ({ templateId } = {}, { dispatch, rejectWithValue }) => {
    dispatch(templatesErrorCleared());
    try {
      const response = await fetchMessageTemplateByIdApi(templateId);
      return response;
    } catch (error) {
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue({ message: error?.message, status: error?.status });
    }
  },
);

export const updateMessageTemplate = createAsyncThunk(
  'templates/update',
  async ({ templateId, payload } = {}, { dispatch, rejectWithValue }) => {
    dispatch(templatesErrorCleared());
    try {
      const response = await updateMessageTemplateApi(templateId, payload ?? {});
      return response;
    } catch (error) {
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);

export const setMessageTemplateStatus = createAsyncThunk(
  'templates/setStatus',
  async ({ templateId, status } = {}, { dispatch, rejectWithValue }) => {
    dispatch(templatesErrorCleared());
    try {
      const response = await setMessageTemplateStatusApi(templateId, status);
      return response;
    } catch (error) {
      dispatch(templatesErrorSet(error?.message));
      return rejectWithValue(error?.message);
    }
  },
);
